import { useEffect, useState, type ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import type { ErpSettings } from '../../contract'
import { HowItWorks } from '../../ui/calm'
import { RULE_LIMITS } from '../settings-logic'
import { fieldInput, fmtDateTime, NumberField, SettingRow, SettingsGroup, SettingsScreen, Switch } from '../ui'

/* Configurações › Organização › Dados da organização: settings.org (nome, sede, fuso, sessão num formulário de 2
   colunas; SSO e 2FA em linhas com interruptor). */

const TIMEZONES = ['America/Sao_Paulo', 'America/Manaus', 'America/Belem', 'America/Fortaleza', 'America/Recife', 'America/Cuiaba', 'America/Rio_Branco', 'America/Noronha'] as const

export function OrgSection() {
    return (
        <SettingsScreen
            title="Dados da organização"
            head={s => ({
                title: `${s.org.name}${s.org.city ? `, em ${s.org.city}` : ''}.`,
                sub: `Última mudança nas configurações: ${fmtDateTime(s.updatedAt)}${s.updatedBy ? ` · ${s.updatedBy}` : ''}`,
            })}
        >
            {(s, save, saving) => {
                const set = (org: Partial<ErpSettings['org']>) => save({ org: { ...s.org, ...org } })
                return (
                    <>
                        <SettingsGroup id="og-ident" title="Organização" aside="aparece em recibos e e-mails">
                            <div className="grid gap-x-5 gap-y-3 py-1" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))' }}>
                                <Field label="Nome" htmlFor="og-name">
                                    <TextField id="og-name" value={s.org.name} label="Nome da organização" min={2} disabled={saving} onCommit={name => set({ name })} />
                                </Field>
                                <Field label="Cidade sede" htmlFor="og-city">
                                    <TextField id="og-city" value={s.org.city} label="Cidade sede" min={0} disabled={saving} onCommit={city => set({ city })} />
                                </Field>
                                <Field label="Fuso horário" hint="Usado nos prazos, alertas e relatórios." htmlFor="og-tz">
                                    <select id="og-tz" value={s.org.timezone} disabled={saving} aria-label="Fuso horário" onChange={e => set({ timezone: e.target.value })} className={`${fieldInput} w-full`}>
                                        {[...new Set([s.org.timezone, ...TIMEZONES])].map(tz => <option key={tz} value={tz}>{tz.replace('America/', '').replace('_', ' ')}</option>)}
                                    </select>
                                </Field>
                                <Field label="Duração da sessão" hint="Depois desse tempo a pessoa entra de novo.">
                                    <NumberField value={s.org.sessionHours} {...RULE_LIMITS.sessionHours} unit="horas" label="Duração da sessão, em horas" disabled={saving} onCommit={sessionHours => set({ sessionHours })} />
                                </Field>
                            </div>
                        </SettingsGroup>
                        <SettingsGroup id="og-login" title="Login e segurança" aside="vale para todas as frentes">
                            <div>
                                <SettingRow title="Entrar com Microsoft (SSO)" hint="Usa a conta corporativa Microsoft; a senha não fica no sistema.">
                                    <Switch checked={s.org.ssoMicrosoft} disabled={saving} label="Entrar com Microsoft (SSO)" onChange={ssoMicrosoft => set({ ssoMicrosoft })} />
                                </SettingRow>
                                <SettingRow title="2FA obrigatório" hint="Segundo fator para todos; administradores sempre.">
                                    <Switch checked={s.org.require2fa} disabled={saving} label="2FA obrigatório" onChange={require2fa => set({ require2fa })} />
                                </SettingRow>
                            </div>
                        </SettingsGroup>
                        <HowItWorks>
                            <p className="m-0">Cada campo salva sozinho ao sair dele (ou com Enter) e fica na Auditoria.</p>
                            <p className="m-0">As metas (Live Concert, campo e rateio) ficam em <Link to="/configuracoes/$section" params={{ section: 'regras' }} className="text-[var(--green)] underline">Regras e alertas</Link>.</p>
                            <p className="m-0">Login e sessão aqui são a política do ERP. O login do Panel continua seguindo a configuração do servidor (Microsoft Entra e o limite de sessão dele) até a Tecnologia alinhar os dois.</p>
                        </HowItWorks>
                    </>
                )
            }}
        </SettingsScreen>
    )
}

/** Campo do formulário: rótulo em cima, controle e, se houver, uma dica apagada. */
function Field({ label, hint, htmlFor, children }: { label: string; hint?: string; htmlFor?: string; children: ReactNode }) {
    return (
        <div className="flex min-w-0 flex-col gap-1">
            {htmlFor ? <label htmlFor={htmlFor} className="text-[12.5px] font-medium text-fg">{label}</label> : <span className="text-[12.5px] font-medium text-fg">{label}</span>}
            {children}
            {hint && <span className="text-[12px] text-muted">{hint}</span>}
        </div>
    )
}

function TextField({ id, value, label, min, disabled, onCommit }: { id?: string; value: string; label: string; min: number; disabled?: boolean; onCommit: (v: string) => void }) {
    const [text, setText] = useState(value)
    useEffect(() => setText(value), [value])
    const commit = () => {
        const v = text.trim()
        if (v.length < min || v.length > 120) return setText(value)
        if (v !== value) onCommit(v)
    }
    return <input id={id} type="text" value={text} maxLength={120} disabled={disabled} aria-label={label} onChange={e => setText(e.target.value)} onBlur={commit} onKeyDown={e => { if (e.key === 'Enter') commit() }} className={`${fieldInput} w-full`} />
}
